import { z } from "zod";
import { localizedTextSchema, requiredLocalizedTextSchema } from "./common";

/**
 * Pricing leaf for one package. Mirrors the `prices` table.
 *
 * - `type` is the typePrice enum (0=per-person, 1=per-hour, 2=per-night, 3=per-group/flat).
 * - All money fields are integers in MAD (whole units).
 * - `adult` / `child` / `infant`: `-1` = this guest category is not available
 *   for the package (web + mobile pickers gray the row out and force the
 *   count to 0), `0` = free, `> 0` = price per guest.
 * - `transport` tri-state: `null` = pickup not offered by this package,
 *   `0` = pickup included free, `> 0` = optional paid add-on at this price.
 * - `transportOutside` is a flat surcharge added to the resolved pickup price
 *   when the guest stays outside the activity's city. `null`/0 = none.
 * - `extraGuest` (per-vehicle packages only) is the price for each rider
 *   beyond the package's `includedGuests` sharing the same unit — a second
 *   person on a 300cc quad. `null`/0 = sharing is not sold.
 */
const guestPriceSchema = z.union([z.literal(-1), z.number().nonnegative()]);

export const packagePricingSchema = z.object({
  type: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]).default(0),
  label: z.string().nullable().optional(),
  adult: guestPriceSchema.default(0),
  child: guestPriceSchema.default(0),
  infant: guestPriceSchema.default(0),
  transport: z.number().nonnegative().nullable().default(null),
  transportLarge: z.number().nonnegative().nullable().optional(),
  transportOutside: z.number().nonnegative().nullable().optional(),
  extraGuest: z.number().nonnegative().nullable().optional(),
});
export type PackagePricing = z.infer<typeof packagePricingSchema>;

/**
 * One package + its pricing as the admin/web see it.
 */
export const packageDtoSchema = z.object({
  id: z.string().uuid(),
  activityId: z.string().uuid(),
  name: localizedTextSchema,
  description: localizedTextSchema,
  imageUrl: z.string().nullable(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  /** Exact departures ("HH:mm"). Beats the start/end window when non-empty. */
  timeSlots: z.array(z.string()).nullable().optional(),
  /** The start/end window is one departure at an hour confirmed later
   * ("13:00–15:00"), not hourly slots. */
  flexibleTime: z.boolean().optional(),
  maxGuests: z.number().int().nullable(),
  /** Riders the flat per-vehicle rate covers. `null` = the whole unit. */
  includedGuests: z.number().int().nullable().optional(),
  /** This option's own commission rate (read with the activity's type).
   * `null` = the activity's rate. */
  commission: z.number().int().nullable().optional(),
  isMostPopular: z.boolean(),
  pricing: packagePricingSchema,
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  deletedAt: z.string().nullable(),
});
export type PackageDTO = z.infer<typeof packageDtoSchema>;

export const packageUpsertSchema = z.object({
  /** Present on existing packages; absent on new ones. */
  id: z.string().uuid().optional(),
  name: requiredLocalizedTextSchema,
  description: localizedTextSchema.optional(),
  imageUrl: z.string().nullable().optional(),
  startTime: z.string().nullable().optional(),
  endTime: z.string().nullable().optional(),
  /** Exact departures ("HH:mm"). Beats the start/end window when non-empty. */
  timeSlots: z.array(z.string()).nullable().optional(),
  /** The start/end window is one departure at an hour confirmed later
   * ("13:00–15:00"), not hourly slots. */
  flexibleTime: z.boolean().optional(),
  maxGuests: z.number().int().positive().nullable().optional(),
  includedGuests: z.number().int().positive().nullable().optional(),
  commission: z.number().int().nonnegative().nullable().optional(),
  isMostPopular: z.boolean().default(false),
  pricing: packagePricingSchema,
});
export type PackageUpsertInput = z.infer<typeof packageUpsertSchema>;

/**
 * Body shape of `PUT /activities/:id/packages` — full replace of packages
 * for the given activity. Existing rows with matching ids are updated;
 * unsent ids are soft-deleted; new ones are inserted.
 */
export const packagesBulkUpsertSchema = z.object({
  packages: z.array(packageUpsertSchema),
});
export type PackagesBulkUpsertInput = z.infer<typeof packagesBulkUpsertSchema>;
